import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Error404 } from '@/components/ErrorState/Error404'
import { Loader } from '@/components/Loader/Loader'
import { AppRoutes } from '@/constants/appRoutes'
import { EUserStatus } from '@/constants/userStatus'
import { useAppDispatch } from '@/hooks/redux'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { api } from '@/store/api'
import { usePaymentLinkLoginMutation } from '@/store/api/Auth/Auth.query'
import { setSession } from '@/store/api/Auth/Auth.slice'
import { identifyUser } from '@/utils/analytics/analytics'
import { AuthMethods } from '@/utils/analytics/analyticsConstants'

export const PaymentRedirect = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [searchParams] = useSearchParams()

  const [paymentLinkLogin, { isLoading, isError }] =
    usePaymentLinkLoginMutation()

  useEffect(() => {
    const handlePaymentRedirect = async () => {
      const sessionId = searchParams.get('session_id')

      if (!sessionId) {
        catchWithSentry(new Error('Missing session_id parameter'))
        setTimeout(() => navigate(AppRoutes.login), 3000)
        return
      }

      try {
        dispatch(api.util.resetApiState())

        const response = await paymentLinkLogin({ sessionId }).unwrap()

        dispatch(setSession(response.info))

        identifyUser(response.info.email, {
          user_id: response.info.id,
          email: response.info.email,
          user_status: response.info.userStatus,
          method: AuthMethods.VSL,
        })

        // Redirect based on user status
        if (response.info.userStatus === EUserStatus.PENDING_SET_PASSWORD) {
          navigate(AppRoutes.setPassword)
        } else {
          navigate(AppRoutes.dashboard)
        }
      } catch (error) {
        catchWithSentry(error)
        setTimeout(() => navigate(AppRoutes.login), 3000)
      }
    }

    handlePaymentRedirect()
  }, [searchParams, navigate, dispatch, paymentLinkLogin])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader />
          <p className="text-lg font-medium text-neutralColor">
            {t('payment.redirect.processing')}
          </p>
          <p className="text-sm text-neutralColor60">
            {t('payment.redirect.pleaseWait')}
          </p>
        </div>
      </div>
    )
  }

  if (isError) {
    return (
      <Error404
        title={t('payment.redirect.errorTitle')}
        text={t('payment.redirect.errorMessage')}
        absolute={true}
      />
    )
  }

  return null
}
